/**
 * Columns and ordering for the Students table (rows of
 * GET /api/organisations/students) and for the Overview's drill-downs,
 * which render the same table with their own column sets: the Needs
 * attention buckets over tracker dashboard rows, Most engaged over ranked
 * Students list rows, and the documents view over GET
 * /api/organisations/documents rows.
 *
 * Owns, per column, what it sorts by (`value`), which way a first click
 * sorts it, what a cell shows (`render`, else the value) and what the CSV
 * export writes (`csv`: one or more labelled cells). Deliberately does not
 * score, rank or weight anything: every column is a value the server sent,
 * and the order is only ever the one the teacher clicked (Most engaged's
 * rank is lib/derive's compareEngagement, the card's own order).
 */
import React from 'react';
import { COMPLETION_KINDS } from './completions';
import { DOCUMENT_TYPES, documentsHeld } from './documents';
import { daysSince, relativeDays, orDash, shortDate, EM_DASH } from './format';
import { DocumentCell, DoneDots, MixBar } from '../components/overview/cells';

const timeOf = (iso) => {
  const t = iso ? new Date(iso).getTime() : NaN;
  return Number.isNaN(t) ? null : t;
};

const lastActiveTime = (s) => timeOf(s.last_active_at);

const dateOrDash = (iso) => (iso ? shortDate(iso) : EM_DASH);

const csvDate = (iso) => (iso ? String(iso).slice(0, 10) : null);

const formsText = (s) => (s.forms && s.forms.length ? s.forms.join(', ') : EM_DASH);

/** What a student last did, from the tracker's last_activity_kind key. */
export const LAST_ACTIVITY_LABELS = {
  quiz: 'Quiz',
  paper: 'Past or mock paper',
  practice: 'Practice topic',
  interview: 'Interview',
  video_interview: 'Video interview',
  text_interview: 'Text interview',
  case_study: 'Case study',
  case_assessment: 'Case quiz, tutorial or discussion',
  mastery: 'Mastery app',
  reasoning_test: 'Reasoning test',
  application_builder: 'Application builder',
  chat: 'Chat',
  other: 'Other activity',
};

const lastActivityLabel = (s) => (
  s.last_activity_kind ? LAST_ACTIVITY_LABELS[s.last_activity_kind] || LAST_ACTIVITY_LABELS.other : null
);

/** Questions attempted, as the Overview's tile adds them: quiz generator and past papers. */
export const questionsAttempted = (s) => (Number(s.qg_attempted) || 0) + (Number(s.pp_attempted) || 0);

// --- shared columns -----------------------------------------------------------------

// The student column: the table draws a name button (opening the profile)
// over the email. Exported as Name and Email.
const STUDENT = {
  key: 'name',
  label: 'Student',
  numeric: false,
  firstDirection: 'asc',
  isStudent: true,
  value: (s) => s.name || '',
  csv: [{ label: 'Name', value: (s) => s.name }, { label: 'Email', value: (s) => s.email }],
};

const FORM = {
  key: 'form',
  label: 'Form',
  numeric: false,
  firstDirection: 'asc',
  value: (s) => (s.forms && s.forms.length ? s.forms.join(', ') : null),
  render: formsText,
  csv: [{ label: 'Form', value: (s) => (s.forms || []).join(', ') }],
};

// Lifetime last activity (tracker dashboard rows), not filtered by the period.
const LAST_ACTIVE_EVER = {
  key: 'last_active',
  label: 'Last active',
  numeric: false,
  firstDirection: 'desc',
  value: (s) => timeOf(s.last_active_ever_at),
  render: (s) => dateOrDash(s.last_active_ever_at),
  csv: [{ label: 'Last active', value: (s) => csvDate(s.last_active_ever_at) }],
};

// "Never" is display text for no sign-in; the sort value stays null, so it sorts last.
const LAST_SIGNED_IN = {
  key: 'last_signed_in',
  label: 'Last signed in',
  numeric: false,
  firstDirection: 'desc',
  value: (s) => timeOf(s.last_signed_in_at),
  render: (s) => (s.last_signed_in_at ? shortDate(s.last_signed_in_at) : 'Never'),
  csv: [{ label: 'Last signed in', value: (s) => csvDate(s.last_signed_in_at) }],
};

const QUESTIONS = {
  key: 'questions',
  label: 'Questions attempted',
  numeric: true,
  firstDirection: 'desc',
  value: questionsAttempted,
  csv: [{ label: 'Questions attempted', value: questionsAttempted }],
};

// Items so far: the student's completions since each account opened, joined
// on from the Students list for period "all" (null until it has loaded).
const ITEMS_SO_FAR = {
  key: 'items',
  label: 'Items so far',
  numeric: true,
  firstDirection: 'desc',
  value: (s) => s.items_so_far ?? null,
  render: (s) => orDash(s.items_so_far),
  csv: [{ label: 'Items so far', value: (s) => s.items_so_far }],
};

// --- the Students tab -----------------------------------------------------------------

// `value` returns null for "no value"; those rows always sort last, in
// either direction, so "never active" never tops a most-recent-first list.
export const STUDENT_COLUMNS = [
  STUDENT,
  {
    key: 'leaving',
    label: 'Leaving',
    numeric: false,
    firstDirection: 'asc',
    value: (s) => s.cohort_year ?? null,
    render: (s) => orDash(s.cohort_year),
    csv: [
      { label: 'Leaving year', value: (s) => s.cohort_year },
      { label: 'Year group', value: (s) => s.year_group },
    ],
  },
  {
    key: 'last_active',
    label: 'Last active',
    numeric: false,
    firstDirection: 'desc',
    value: lastActiveTime,
    render: (s) => relativeDays(daysSince(s.last_active_at)),
    csv: [{ label: 'Last active', value: (s) => csvDate(s.last_active_at) }],
  },
  ...COMPLETION_KINDS.map((kind) => ({
    key: kind.key,
    label: kind.label,
    numeric: true,
    firstDirection: 'desc',
    value: (s) => s.completions[kind.key],
    className: (s) => `td-table__num${s.completions[kind.key] === 0 ? ' td-table__num--zero' : ''}`,
    csv: [{ label: kind.label, value: (s) => s.completions[kind.key] }],
  })),
];

export const DEFAULT_SORT = { key: 'name', direction: 'asc' };

// --- the Needs attention buckets (tracker dashboard rows) ----------------------------------

export const NEVER_STARTED_COLUMNS = [
  STUDENT,
  FORM,
  {
    key: 'account_created',
    label: 'Account created',
    numeric: false,
    firstDirection: 'asc',
    value: (s) => timeOf(s.account_created_at),
    render: (s) => dateOrDash(s.account_created_at),
    csv: [{ label: 'Account created', value: (s) => csvDate(s.account_created_at) }],
  },
  LAST_SIGNED_IN,
];

export const INACTIVE_COLUMNS = [
  STUDENT,
  FORM,
  LAST_ACTIVE_EVER,
  {
    key: 'days_inactive',
    label: 'Days inactive',
    numeric: true,
    firstDirection: 'desc',
    value: (s) => s.days_inactive ?? null,
    render: (s) => orDash(s.days_inactive),
    csv: [{ label: 'Days inactive', value: (s) => s.days_inactive }],
  },
  {
    key: 'last_activity',
    label: 'Last thing they did',
    numeric: false,
    firstDirection: 'asc',
    value: lastActivityLabel,
    render: (s) => orDash(lastActivityLabel(s)),
    csv: [{ label: 'Last thing they did', value: lastActivityLabel }],
  },
  ITEMS_SO_FAR,
  LAST_SIGNED_IN,
];

const weakestText = (w) => (w ? `${w.exam} · ${w.accuracy}% of ${w.answered}` : null);

export const LOW_ACCURACY_COLUMNS = [
  STUDENT,
  FORM,
  QUESTIONS,
  {
    key: 'accuracy',
    label: 'Accuracy',
    numeric: true,
    firstDirection: 'asc',
    value: (s) => s.qg_accuracy ?? null,
    render: (s) => (s.qg_accuracy == null ? EM_DASH : `${s.qg_accuracy}%`),
    csv: [{ label: 'Quiz accuracy (%)', value: (s) => s.qg_accuracy }],
  },
  {
    key: 'weakest_exam',
    label: 'Weakest exam',
    numeric: false,
    firstDirection: 'asc',
    value: (s) => (s.weakest_exam ? s.weakest_exam.accuracy : null),
    render: (s) => orDash(weakestText(s.weakest_exam)),
    csv: [
      { label: 'Weakest exam', value: (s) => s.weakest_exam?.exam },
      { label: 'Weakest exam accuracy (%)', value: (s) => s.weakest_exam?.accuracy },
      { label: 'Weakest exam questions', value: (s) => s.weakest_exam?.answered },
    ],
  },
  LAST_ACTIVE_EVER,
];

export const NO_DOCUMENTS_COLUMNS = [
  STUDENT,
  FORM,
  QUESTIONS,
  ITEMS_SO_FAR,
  LAST_ACTIVE_EVER,
];

// Each bucket's columns and its first sort (the spec's table).
export const BUCKET_TABLES = {
  never_started: { columns: NEVER_STARTED_COLUMNS, sort: { key: 'account_created', direction: 'asc' } },
  inactive_30d: { columns: INACTIVE_COLUMNS, sort: { key: 'days_inactive', direction: 'desc' } },
  low_accuracy: { columns: LOW_ACCURACY_COLUMNS, sort: { key: 'accuracy', direction: 'asc' } },
  no_documents: { columns: NO_DOCUMENTS_COLUMNS, sort: { key: 'items', direction: 'desc' } },
};

// --- Most engaged (ranked Students list rows) ---------------------------------------------

// Items sorts by rank, so descending is the card's own order (rank 1 first,
// with its tie-breaks) and ascending is that order reversed.
export const ENGAGED_COLUMNS = [
  {
    key: 'rank',
    label: 'Rank',
    numeric: true,
    firstDirection: 'asc',
    value: (s) => s.rank,
    className: () => 'td-table__num td-table__rank',
    csv: [{ label: 'Rank', value: (s) => s.rank }],
  },
  STUDENT,
  FORM,
  {
    key: 'items',
    label: 'Items',
    numeric: true,
    firstDirection: 'desc',
    value: (s) => -s.rank,
    render: (s) => s.items,
    csv: [{ label: 'Items', value: (s) => s.items }],
  },
  {
    key: 'mix',
    label: 'Mix',
    numeric: false,
    firstDirection: 'desc',
    sortable: false,
    value: () => null,
    render: (s) => <MixBar completions={s.completions} />,
    csv: COMPLETION_KINDS.map((kind) => ({ label: kind.label, value: (s) => s.completions[kind.key] })),
  },
  {
    key: 'active_days',
    label: 'Active days',
    numeric: true,
    firstDirection: 'desc',
    value: (s) => s.active_days ?? null,
    render: (s) => orDash(s.active_days),
    csv: [{ label: 'Active days', value: (s) => s.active_days }],
  },
  {
    key: 'last_active',
    label: 'Last active',
    numeric: false,
    firstDirection: 'desc',
    value: lastActiveTime,
    render: (s) => relativeDays(daysSince(s.last_active_at)),
    csv: [{ label: 'Last active', value: (s) => csvDate(s.last_active_at) }],
  },
];

export const ENGAGED_SORT = { key: 'items', direction: 'desc' };

// --- the documents view (GET /api/organisations/documents rows) ----------------------------------

const docStatus = (doc) => (doc ? 'Uploaded' : 'Not uploaded');

// No URL is ever exported: status, version and date only.
export const DOCUMENT_COLUMNS = [
  STUDENT,
  FORM,
  {
    key: 'done',
    label: 'Done',
    numeric: false,
    firstDirection: 'asc',
    value: documentsHeld,
    render: (s) => <DoneDots row={s} />,
    csv: [{ label: 'Documents on file', value: documentsHeld }],
  },
  ...DOCUMENT_TYPES.map((type) => ({
    key: type.key,
    label: type.label,
    numeric: false,
    firstDirection: 'desc',
    value: (s) => timeOf(s.documents?.[type.key]?.created_at),
    render: (s) => <DocumentCell studentId={s.id} studentName={s.name} type={type} doc={s.documents?.[type.key]} />,
    csv: [
      { label: `${type.label} status`, value: (s) => docStatus(s.documents?.[type.key]) },
      { label: `${type.label} version`, value: (s) => s.documents?.[type.key]?.version },
      { label: `${type.label} date`, value: (s) => csvDate(s.documents?.[type.key]?.created_at) },
    ],
  })),
];

export const DOCUMENT_SORT = { key: 'done', direction: 'asc' };

// --- ordering -----------------------------------------------------------------------

const byKey = (columns) => new Map(columns.map((c) => [c.key, c]));

/** The sort after clicking `key`: the same column flips, a new one starts
 * at its own first direction. */
export function nextSort(current, key, columns = STUDENT_COLUMNS) {
  if (current.key === key) {
    return { key, direction: current.direction === 'asc' ? 'desc' : 'asc' };
  }
  return { key, direction: byKey(columns).get(key).firstDirection };
}

const byName = (a, b) => (a.name || '').localeCompare(b.name || '');

/** A sorted copy. Ties, and rows with no value, fall back to name order.
 * An unknown key sorts by the student column. */
export function sortStudents(students, { key, direction }, columns = STUDENT_COLUMNS) {
  const keyed = byKey(columns);
  const column = keyed.get(key) || keyed.get(DEFAULT_SORT.key) || columns[0];
  const sign = direction === 'desc' ? -1 : 1;
  return [...students].sort((a, b) => {
    const x = column.value(a);
    const y = column.value(b);
    if (x == null && y == null) return byName(a, b);
    if (x == null) return 1;
    if (y == null) return -1;
    const diff = typeof x === 'string' ? x.localeCompare(y) : x - y;
    return diff !== 0 ? sign * diff : byName(a, b);
  });
}
